import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMic } from "react-icons/fi";
import "../../styles/VoiceInputButton.css";

export default function VoiceInputButton({
  loading = false,
  disabled = false,
  lang = "uk-UA",
  onTranscript,
  onListeningChange,
}) {
  const { t } = useTranslation("composer");

  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  const SpeechRecognition = useMemo(() => {
    if (typeof window === "undefined") return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
  }, []);

  const hasSpeech = !!SpeechRecognition;

  const stopVoice = () => {
    try {
      recogRef.current?.stop();
    } catch {}
  };

  const startVoice = () => {
    if (!hasSpeech || loading || disabled) return;

    if (listening) {
      stopVoice();
      return;
    }

    const recognition = new SpeechRecognition();
    recogRef.current = recognition;

    recognition.lang = lang;
    recognition.interimResults = true;
    recognition.continuous = false;

    let lastFinal = "";

    recognition.onstart = () => {
      setListening(true);
      onListeningChange?.(true);
    };

    recognition.onresult = (e) => {
      let finalText = "";
      let interimText = "";

      for (let i = e.resultIndex; i < e.results.length; i++) {
        const txt = e.results[i][0]?.transcript || "";
        if (e.results[i].isFinal) finalText += txt;
        else interimText += txt;
      }

      if (finalText.trim()) {
        lastFinal = finalText.trim();
        onTranscript?.(lastFinal, { final: true });
        return;
      }

      const next = interimText.trim();
      if (next) {
        onTranscript?.(
          lastFinal ? `${lastFinal} ${next}`.trim() : next,
          { final: false }
        );
      }
    };

    recognition.onerror = (e) => {
      console.log("Speech recognition error:", e?.error || e);
      setListening(false);
      onListeningChange?.(false);
      recogRef.current = null;
    };

    recognition.onend = () => {
      setListening(false);
      onListeningChange?.(false);
      recogRef.current = null;
    };

    recognition.start();
  };

  useEffect(() => {
    return () => {
      stopVoice();
    };
  }, []);

  return (
    <button
      type="button"
      className={`voiceInputBtn ${listening ? "isListening" : ""}`}
      disabled={loading || disabled || !hasSpeech}
      aria-label={listening ? t("stop_voice_input") : t("voice_input")}
      title={
        !hasSpeech
          ? t("voice_not_supported")
          : listening
          ? t("stop")
          : t("mic")
      }
      onClick={startVoice}
    >
      <span className="voiceInputBtn__icon">
        <FiMic />
      </span>
    </button>
  );
}